import React from 'react';
import PropTypes from 'prop-types';
import { CalendarIcon, MapPinIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { formatPrice } from '../services/formatPrice';

import MultilineText from '../components/MultilineText';
import clsx from 'clsx';
import Badge from '../components/Badge';
import ModulesEnum, { getEventModules } from '../enums/modules';
import sanitizeHtml from 'sanitize-html';
import { getImageSrcAndSrcSet, stripQueryStringFromCdnUrl } from '../services/getUrlFromCdn';
import useCheckoutState, { checkoutLayouts } from '../hooks/useCheckoutState';
import { darken, lighten } from 'polished';
import * as Avatar from '@radix-ui/react-avatar';
import getSafeReadableColor from '../lib/getSafeReadableColor';

import { getMedia } from '../lib/getMedia';
import { formatEventDate } from '../lib/format-date';
import isHexColor from '../lib/is-hex-color';
import { useCheckoutLocalization } from '@repo/checkout/hooks/useCheckoutLocalization';

function getTitle({ allowDynamicTitle, dynamicTitle, title }) {
  if (!allowDynamicTitle) return title;

  if (dynamicTitle) {
    return sanitizeHtml(dynamicTitle.replace(/\\n/g, '\n'));
  }

  return title;
}

function getDescription({
  allowDynamicDescription,
  dynamicDescription,
  productDescription,
  productDescriptionHtml,
  checkoutDescription,
  checkoutDescriptionHtml,
}) {
  if (dynamicDescription && allowDynamicDescription) {
    return sanitizeHtml(dynamicDescription.replace(/\\n/g, '\n'));
  }

  if (typeof productDescriptionHtml === 'string') {
    return productDescriptionHtml;
  } else if (typeof productDescription === 'string') {
    return productDescription;
  } else if (typeof checkoutDescriptionHtml === 'string') {
    return checkoutDescriptionHtml;
  } else if (typeof checkoutDescription === 'string') {
    return checkoutDescription;
  }

  return '';
}

function getColors(checkout, checkoutLayout) {
  if (checkoutLayout === checkoutLayouts.CENTER || checkoutLayout === checkoutLayouts.SPLIT) {
    return {
      primaryTextColor: '#000000',
      secondaryTextColor: '#685357',
      backgroundColor: '#ffffff',
      accentColor: darken(0.1, '#ffffff'),
    };
  } else {
    const primaryTextColor = checkout.primaryTextColor || '#000000';
    const secondaryTextColor = checkout.secondaryTextColor || '#685357';

    const backgroundIsDark =
      getSafeReadableColor(checkout.backgroundColor, 'black', 'white') === 'white';

    if (backgroundIsDark) {
      return {
        primaryTextColor,
        secondaryTextColor,
        backgroundColor: checkout.backgroundColor,
        accentColor: isHexColor(checkout.backgroundColor)
          ? lighten(0.25, checkout.backgroundColor)
          : checkout.backgroundColor,
      };
    } else {
      return {
        primaryTextColor,
        secondaryTextColor,
        backgroundColor: checkout.backgroundColor,
        accentColor: isHexColor(checkout.backgroundColor)
          ? darken(0.25, checkout.backgroundColor)
          : checkout.backgroundColor,
      };
    }
  }
}

const propTypes = {
  checkout: PropTypes.object.isRequired,
  seller: PropTypes.object.isRequired,
};

const EventDetails = () => {
  const { isScreenshot, checkoutLayout, checkout, seller, query, isPreview } = useCheckoutState();
  const { locale } = useCheckoutLocalization();

  const modules = getEventModules({
    checkout,
    seller,
    isScreenshot,
    query,
  });

  const siteUrl = seller.url;

  const ImageWrapper = siteUrl ? 'a' : 'div';

  const isSoldOut = checkout.eventDetails?.isSoldOut;
  const isCanceled = checkout.eventDetails?.status === 'canceled';

  const title = getTitle({
    allowDynamicTitle: checkout.allowDynamicTitle,
    title: checkout.title,
    dynamicTitle: query.title,
  });

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  const { primaryTextColor, accentColor } = getColors(checkout, checkoutLayout);

  const media = getMedia({ checkout });
  const hasMedia = media && media.length > 0;

  return (
    <div>
      <div
        className={clsx('cp_product-wrapper flex flex-col w-full gap-y-2', {
          'mt-10':
            modules[ModulesEnum.LOGO] && !hasMedia && checkoutLayout === checkoutLayouts.CENTER,
          'pt-4': checkoutLayout === checkoutLayouts.CENTER && !hasMedia,
        })}
        style={{
          color: primaryTextColor,
        }}
      >
        {modules[ModulesEnum.LOGO] && checkoutLayout === checkoutLayouts.CENTER && (
          <div className={clsx('cp_product-logo-wrapper px-4 relative pt-4')}>
            <ImageWrapper
              className={clsx(
                'cp_product-logo block rounded-full w-20 h-20 overflow-hidden -top-14 left-0 right-0 mx-auto absolute shadow-sm text-5xl',
                {
                  'hover:shadow': !!siteUrl,
                },
              )}
              rel="nofollow noreferrer"
              href={siteUrl}
              target="_blank"
            >
              <Logo width="80" height="80" />
            </ImageWrapper>
          </div>
        )}

        {(modules[ModulesEnum.TITLE] ||
          modules[ModulesEnum.PRICE] ||
          modules[ModulesEnum.BUSINESS_NAME]) && (
          <div
            className={clsx(`cp_product-content`, {
              'text-center': checkout.textAlign === 'center',
            })}
          >
            <div className="cp_product-title-wrapper justify-between items-start space-y-1">
              {modules[ModulesEnum.TITLE] && (
                <div className="cp_product-title-container">
                  <h1 className="cp_product-title text-2xl font-bold leading-tight break-words">
                    {title}
                  </h1>
                </div>
              )}
            </div>

            {isCanceled ? (
              <Badge className="cp_canceled text-sm bg-red-600 text-white">
                {checkout.canceledText}
              </Badge>
            ) : (
              isSoldOut && (
                <Badge className="cp_sold-out text-sm bg-gray-900 text-white">
                  {checkout.soldOutText}
                </Badge>
              )
            )}

            {modules[ModulesEnum.BUSINESS_NAME] && checkoutLayout === checkoutLayouts.CENTER && (
              <MerchantName />
            )}
          </div>
        )}

        {(modules[ModulesEnum.DATE] ||
          modules[ModulesEnum.LOCATION] ||
          modules[ModulesEnum.MEETING_LINK]) && (
          <div className="cp_event-details-wrapper flex flex-col gap-y-1">
            {modules[ModulesEnum.DATE] && (
              <div
                className={clsx('cp_event-details-date-wrapper flex items-start gap-x-2', {
                  'justify-start': checkout.textAlign === 'left',
                  'justify-center': checkout.textAlign === 'center',
                })}
              >
                <div className="cp_event-details-date-icon-wrapper mt-0.5">
                  <CalendarIcon className="cp_event-details-date-icon h-5 w-5" />
                </div>
                <div className="cp_event-details-date font-medium flex flex-col">
                  {formatEventDate({
                    startDate: checkout.eventDetails.startDate,
                    endDate: checkout.eventDetails.endDate,
                    timezone: checkout.eventDetails.timezone,
                    locale,
                  }).map(date => (
                    <span key={date}>{date}</span>
                  ))}
                </div>
              </div>
            )}

            {modules[ModulesEnum.LOCATION] &&
              (checkout.eventDetails.type === 'in_person' ||
                checkout.eventDetails.type === 'hybrid') && (
                <div
                  className={clsx('cp_event-details-location-wrapper flex items-center gap-x-2', {
                    'justify-start': checkout.textAlign === 'left',
                    'justify-center': checkout.textAlign === 'center',
                  })}
                >
                  <div className="cp_event-details-location-icon-wrapper">
                    <MapPinIcon className="cp_event-details-location-icon h-5 w-5" />
                  </div>
                  {checkout.eventDetails.mapsLink && (
                    <a
                      href={checkout.eventDetails.mapsLink}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="cp_event-details-location-link underline"
                    >
                      <span className="cp_event-details-location font-medium">
                        {checkout.eventDetails.location}
                      </span>
                    </a>
                  )}

                  {!checkout.eventDetails.mapsLink && (
                    <span className="cp_event-details-location font-medium">
                      {checkout.eventDetails.location}
                    </span>
                  )}
                </div>
              )}

            {modules[ModulesEnum.MEETING_LINK] &&
              (checkout.eventDetails.type === 'virtual' ||
                checkout.eventDetails.type === 'hybrid') && (
                <div
                  className={clsx('cp_event-details-virtual-wrapper flex items-center gap-x-2', {
                    'justify-start': checkout.textAlign === 'left',
                    'justify-center': checkout.textAlign === 'center',
                  })}
                >
                  <div className="cp_event-details-virtual-icon-wrapper">
                    <VideoCameraIcon className="cp_event-details-virtual-icon h-5 w-5" />
                  </div>
                  <span className="cp_event-details-virtual font-medium">Virtual event</span>
                </div>
              )}
          </div>
        )}

        {modules[ModulesEnum.DESCRIPTION] && (
          <div
            className={clsx('cp_product-description-wrapper overflow-hidden', {
              'text-center': checkout.textAlign === 'center',
              ...(checkoutLayout === checkoutLayouts.DOUBLE && {
                color: primaryTextColor,
              }),
            })}
          >
            <style
              dangerouslySetInnerHTML={{
                __html: `
                    body {
                      --store-code-accent-color: ${accentColor};
                    }
                `,
              }}
            />

            <MultilineText
              className="cp_product-description"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

EventDetails.propTypes = propTypes;

export default EventDetails;

export function StoreNameWithLabel() {
  const { seller, checkout, checkoutLayout } = useCheckoutState();

  const { primaryTextColor } = getColors(checkout, checkoutLayout);

  const siteUrl = seller.url;

  if (!checkout.showStoreNameWithLabel) {
    return null;
  }

  return (
    <div className="cp_sold-by-wrapper mb-4">
      {siteUrl ? (
        <a
          href={siteUrl}
          target="_blank"
          rel="nofollow noreferrer"
          className={clsx('flex gap-x-2 items-center mt-px', {
            'justify-center': checkout.logoAlign === 'center',
          })}
        >
          <div className="w-7 h-7 rounded-full overflow-hidden">
            <Logo width="28" height="28" />
          </div>

          <div
            style={{
              color: primaryTextColor,
            }}
            className="font-medium cp_sold-by-seller"
          >
            {seller.displayName}
          </div>
        </a>
      ) : (
        <div
          className={clsx('flex gap-x-2 items-center mb-6 mt-px', {
            'mb-6':
              checkoutLayout === checkoutLayouts.DOUBLE || checkoutLayout === checkoutLayouts.SPLIT,
            'justify-center': checkout.logoAlign === 'center',
          })}
        >
          <div className="cp_sold-by-logo w-7 h-7 rounded-full overflow-hidden">
            <Logo width="28" height="28" />
          </div>

          <div
            style={{
              color: primaryTextColor,
            }}
            className="cp_sold-by-seller font-medium"
          >
            {seller.displayName}
          </div>
        </div>
      )}
    </div>
  );
}

function MerchantName() {
  const { seller } = useCheckoutState();
  const siteUrl = seller.url;

  return (
    <div className="cp_sold-by-wrapper mt-1">
      {siteUrl ? (
        <a
          href={siteUrl}
          className="cp_sold-by-seller font-medium underline"
          target="_blank"
          rel="nofollow noreferrer"
        >
          {seller.displayName}
        </a>
      ) : (
        <span className="cp_sold-by-seller font-medium">{seller.displayName}</span>
      )}
    </div>
  );
}

export function PriceWrapper({ children }) {
  return <div className="cp_product-price-wrapper inline-flex gap-x-2">{children}</div>;
}

export function SetupFee({ product, checkout }) {
  if (!product.setupFee) return '';

  return (
    <span className="cp_setup-fee">
      <span className="cp_setup-fee-amount">
        {formatPrice({
          amount: product.setupFee,
          currency: product.currency,
          locale: product.locale,
          style: 'currency',
        })}{' '}
      </span>
      <span className="cp_setup-fee-text">{checkout.setupFeeText}</span>
    </span>
  );
}

export function DiscountedFromPrice({ product, checkout }) {
  const { query } = useCheckoutState();
  const dynamicDiscountedFromPrice = Number(query?.discounted_from);

  const discountedFromPrice =
    checkout.allowDynamicDiscountedFromPrice && dynamicDiscountedFromPrice >= 0
      ? dynamicDiscountedFromPrice
      : product.discountedFromPrice;

  if (!discountedFromPrice) return '';
  if (product.payWhatYouWant) return '';

  return (
    <span>
      <span className="line-through">
        {formatPrice({
          amount: discountedFromPrice,
          currency: product.currency,
          locale: checkout.locale,
          style: 'currency',
        })}
      </span>
      <span> </span>
    </span>
  );
}

const Logo = React.memo(({ width, height }) => {
  const { seller, isPreview } = useCheckoutState();
  const logo = getImageSrcAndSrcSet({ url: seller.logo?.url, width, height, isPreview });

  if (!logo) {
    return (
      <Avatar.Root>
        <Avatar.Fallback
          className="w-full h-full flex rounded-full uppercase items-center justify-center overflow-hidden font-bold leading-loose"
          style={{
            background: seller.brandColor,
            color: getSafeReadableColor(seller.brandColor),
          }}
        >
          {seller.displayName?.charAt(0)}
        </Avatar.Fallback>
      </Avatar.Root>
    );
  }

  return (
    <img
      src={logo.src}
      srcSet={logo.srcSet}
      alt={seller.name}
      className="rounded-full"
      width={width}
      height={height}
      loading="eager"
    />
  );
});
